import React, { useState, useEffect } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';

interface ProviderOption {
  provider_type: string;
  provider_name: string;
  label: string;
  typeLabel?: string;
  envVar: string;
}

interface ApiKeyModalProps {
  provider: ProviderOption | null;
  allProviders?: ProviderOption[];
  isAddMode?: boolean;
  existingConfig?: { id?: string; name?: string; description?: string; tags?: string[]; isGlobal?: boolean } | null;
  onClose: () => void;
  onSave: (data: {
    providerType: string;
    providerName: string;
    name?: string;
    description?: string;
    tags?: string[];
    apiKey?: string;
    isGlobal?: boolean;
  }) => Promise<void>;
  showIsGlobal?: boolean;
}

export const ApiKeyModal: React.FC<ApiKeyModalProps> = ({
  provider,
  allProviders = [],
  isAddMode = false,
  existingConfig = null,
  onClose,
  onSave,
  showIsGlobal = false,
}) => {
  const [selectedProvider, setSelectedProvider] = useState<ProviderOption | null>(provider);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [isGlobal, setIsGlobal] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentProvider = selectedProvider || provider;

  useEffect(() => {
    setSelectedProvider(provider);
  }, [provider]);

  useEffect(() => {
    if (existingConfig) {
      setName(existingConfig.name || '');
      setDescription(existingConfig.description || '');
      setTagsInput((existingConfig.tags || []).join(', '));
      setIsGlobal(existingConfig.isGlobal ?? false);
    } else {
      setName('');
      setDescription('');
      setTagsInput('');
      setIsGlobal(false);
    }
    setApiKey('');
  }, [existingConfig]);

  const parseTags = (s: string): string[] =>
    s
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const p = currentProvider;
    if (!p) return;
    if (!apiKey.trim() && isAddMode) {
      setError('Please enter an API key');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await onSave({
        providerType: p.provider_type,
        providerName: p.provider_name,
        name: name.trim() || undefined,
        description: description.trim() || undefined,
        tags: parseTags(tagsInput).length > 0 ? parseTags(tagsInput) : undefined,
        apiKey: apiKey.trim() || undefined,
        isGlobal: showIsGlobal ? isGlobal : undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!provider) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {isAddMode ? 'Add API Key' : 'Edit API Key'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isAddMode && allProviders.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                <select
                  value={currentProvider ? `${currentProvider.provider_type}:${currentProvider.provider_name}` : ''}
                  onChange={(e) => {
                    const [pt, pn] = e.target.value.split(':');
                    const p = allProviders.find((x) => x.provider_type === pt && x.provider_name === pn);
                    if (p) setSelectedProvider(p);
                  }}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2"
                >
                  {allProviders.map((p) => (
                    <option key={`${p.provider_type}-${p.provider_name}`} value={`${p.provider_type}:${p.provider_name}`}>
                      {p.label} ({p.typeLabel || p.provider_type})
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">Fallback env: {currentProvider?.envVar || ''}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Production OpenAI Key"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What is this key used for?"
                rows={2}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="production, openai, main (comma-separated)"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2"
              />
            </div>

            {showIsGlobal && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isGlobal"
                  checked={isGlobal}
                  onChange={(e) => setIsGlobal(e.target.checked)}
                  className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <label htmlFor="isGlobal" className="text-sm text-gray-700">
                  Set as global key (default for all workspaces)
                </label>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API Key Value {!isAddMode && '(leave blank to keep existing)'}
              </label>
              <div className="relative">
                <input
                  type={showKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={isAddMode ? 'Enter API key...' : '••••••••••••'}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 pl-3 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowKey(!showKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm">{error}</div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
